import { LangUserSessionVerifier } from '../../localization/webmodule-common/other/user-session-verifier-lang';
import { ApiError } from '../../webmodule-common/interop/problem-details';
import { setCurrentUser } from '../../webmodule-common/other/api/current-user';
import {
  strDuplicateLogin,
  strNetworkUnavailable,
  strRequestTokenMismatch,
  strServiceRestarting
} from '../../webmodule-common/other/api/network-consts';
import { tlang } from '../../webmodule-common/other/language/lang';

import { handleOutOfDateIntercept } from '../../webmodule-common/other/common/web-cache';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { showValidations } from '../../webmodule-common/other/ui/modal-validationhandler';
import { pageReload } from '../../webmodule-common/other/ui/resource-resolver';
import { showError } from '../../webmodule-common/other/ui/show-error';
import { ValidationError } from './validation-error';

let handlingDuplicateLogin = false;

// ProblemDetails type patterns
const ProblemTypes = {
  VersionMismatch: 'version-mismatch',
  AlreadyConnected: 'already-connected'
};

//create a generic global handler for communication errors that go unresolved.
//this will typically only be things we dont want to happen not considered normal workflow
export const responseHandler = async (response: ApiError | ValidationError[]) => {
  // Handle validation errors array
  if (Array.isArray(response)) {
    console.log('Service Response Validation Errors Returned');
    const msg: string[] = [];
    response.forEach(err => {
      const errMsg = `${err.property} -> ${err.message}`;
      msg.push(errMsg);
      console.log(errMsg);
    });
    await showValidations(msg, () => tlang`Validation Issues from Server`);
    return;
  }

  // Handle ApiError with ProblemDetails
  if (response instanceof ApiError) {
    const pd = response.problemDetails;
    const detail = pd.detail ?? '';

    // Version mismatch - reload required
    if (pd.type.includes(ProblemTypes.VersionMismatch)) {
      try {
        await handleOutOfDateIntercept(true);
      } finally {
        pageReload();
      }
      return;
    }

    // Already connected / duplicate login (HTTP 412)
    if (pd.type.includes(ProblemTypes.AlreadyConnected) || pd.status === 412 || detail === strDuplicateLogin) {
      if (handlingDuplicateLogin) return;
      handlingDuplicateLogin = true;
      try {
        await information(
          tlang`You are being disconnected as your account is logged into another machine (or Browser)`
        );
        await setCurrentUser(null);
      } finally {
        handlingDuplicateLogin = false;
      }
      return;
    }

    // Request token mismatch - user switched accounts in another tab
    if (detail.includes(strRequestTokenMismatch)) {
      if (handlingDuplicateLogin) return;
      handlingDuplicateLogin = true;
      try {
        await information(LangUserSessionVerifier.userSwitched());
        window.location.reload();
      } finally {
        handlingDuplicateLogin = false;
      }
      return;
    }

    // Network unavailable or service restarting
    if (detail.includes(strNetworkUnavailable) || detail.includes(strServiceRestarting)) {
      console.log('API Response: Network unavailable or service restarting - handled by event source connection state');
      return;
    }

    // Log and display the error
    console.log(`ProblemDetails Error [${pd.status}] "${pd.title}" - ${pd.detail}`);
    if (pd.stackTrace) {
      console.log(`StackTrace: ${pd.stackTrace}`);
    }

    await showError(response, () => tlang`Server Error Occurred`);
    return;
  }

  // Fallback: unknown error type
  console.error('Unknown error type passed to responseHandler:', response);
};
